import { logError } from '@/utils/logger';
import SearchableSelect from '@/components/SearchableSelect';
import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Image as ImageIcon, FilePdf, Funnel, UploadSimple, X, Trash, FileArrowDown } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { SortableHeader, useSort } from '@/components/SortableHeader';

const Reports = () => {
  const { t } = useTranslation();
  const [reports, setReports] = useState([]);
  const [clients, setClients] = useState([]);
  const [zones, setZones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedZone, setSelectedZone] = useState('all');
  const [userPermissions, setUserPermissions] = useState(null);
  const [userRole, setUserRole] = useState('');
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [uploadedPhotos, setUploadedPhotos] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  // Rapport consolidé
  const today = new Date().toISOString().split('T')[0];
  const [aggregateOpen, setAggregateOpen] = useState(false);
  const [aggregateClientId, setAggregateClientId] = useState('');
  const [aggregateFrom, setAggregateFrom] = useState(today);
  const [aggregateTo, setAggregateTo] = useState(today);
  const [aggregateBusy, setAggregateBusy] = useState(false);
  const [formData, setFormData] = useState({
    client_id: undefined,
    site_name: '',
    visit_date: new Date().toISOString().split('T')[0],
    observations: '',
    recommendations: '',
    zone_id: 'zone_kinshasa',
    priority: 'normal',
  });

  // Define useCallback functions BEFORE useEffect that uses them
  const fetchData = useCallback(async () => {
    try {
      const zoneParam = selectedZone && selectedZone !== 'all' ? `?zone_id=${selectedZone}` : '';
      
      const [reportsRes, clientsRes] = await Promise.all([
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/reports${zoneParam}`, { credentials: 'include' }),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/clients${zoneParam}`, { credentials: 'include' }),
      ]);

      if (reportsRes.ok) setReports(await reportsRes.json());
      if (clientsRes.ok) setClients(await clientsRes.json());
    } catch (error) {
      logError('Failed to fetch data:', error);
      toast.error('Erreur lors du chargement');
    }
  }, [selectedZone]);

  const fetchInitialData = useCallback(async () => {
    try {
      const [userRes, zonesRes] = await Promise.all([
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/me`, { credentials: 'include' }),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/zones`, { credentials: 'include' }),
      ]);

      if (userRes.ok) {
        const userData = await userRes.json();
        setUserPermissions({...userData.permissions, effective: userData.effective_permissions || {}});
        setUserRole(userData.role);
        if (!userData.permissions.can_view_all_zones && userData.zone_id) {
          setSelectedZone(userData.zone_id);
        }
      }

      if (zonesRes.ok) {
        const zonesData = await zonesRes.json();
        setZones(zonesData);
        if (zonesData.length > 0) {
          setFormData(prev => ({ ...prev, zone_id: zonesData[0].zone_id }));
        }
      }
    } catch (error) {
      logError('Failed to fetch initial data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial data fetch
  useEffect(() => {
    fetchInitialData();
  }, [fetchInitialData]);

  // Fetch data when zones are loaded or selectedZone changes
  useEffect(() => {
    if (zones.length > 0) {
      fetchData();
    }
  }, [zones.length, selectedZone, fetchData]);

  const handleExportPDF = async (reportId) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/reports/${reportId}/pdf`,
        { credentials: 'include' }
      );
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `rapport_${reportId}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove();
        toast.success('PDF téléchargé');
      } else {
        toast.error('Erreur lors du téléchargement');
      }
    } catch (error) {
      logError('Failed to export PDF:', error);
      toast.error('Erreur lors du téléchargement');
    }
  };

  const handleAggregatePDF = async () => {
    if (!aggregateClientId) { toast.error('Choisissez un client'); return; }
    if (!aggregateFrom || !aggregateTo) { toast.error('Choisissez la période'); return; }
    setAggregateBusy(true);
    try {
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/clients/${aggregateClientId}/reports/aggregate/pdf?date_from=${aggregateFrom}&date_to=${aggregateTo}`;
      const response = await fetch(url, { credentials: 'include' });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        toast.error(err.detail || 'Erreur lors du téléchargement');
        setAggregateBusy(false);
        return;
      }
      const blob = await response.blob();
      const objUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      const clientName = (clients.find(c => c.client_id === aggregateClientId)?.name || 'client').replace(/\s+/g, '_');
      a.href = objUrl;
      a.download = `rapports_consolides_${clientName}_${aggregateFrom}_${aggregateTo}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(objUrl);
      a.remove();
      toast.success('PDF consolidé téléchargé');
      setAggregateOpen(false);
    } catch (error) {
      logError('Aggregate PDF failed:', error);
      toast.error('Erreur lors du téléchargement');
    }
    setAggregateBusy(false);
  };

  const setQuickPeriod = (kind) => {
    const now = new Date();
    if (kind === 'today') {
      const d = now.toISOString().split('T')[0];
      setAggregateFrom(d); setAggregateTo(d);
    } else if (kind === 'week') {
      // Semaine calendaire (Lundi → Dimanche contenant aujourd'hui)
      const d = new Date(now);
      const day = (d.getDay() + 6) % 7; // 0=Lundi
      const monday = new Date(d); monday.setDate(d.getDate() - day);
      const sunday = new Date(monday); sunday.setDate(monday.getDate() + 6);
      setAggregateFrom(monday.toISOString().split('T')[0]);
      setAggregateTo(sunday.toISOString().split('T')[0]);
    } else if (kind === 'month') {
      const first = new Date(now.getFullYear(), now.getMonth(), 1);
      const last = new Date(now.getFullYear(), now.getMonth() + 1, 0);
      setAggregateFrom(first.toISOString().split('T')[0]);
      setAggregateTo(last.toISOString().split('T')[0]);
    }
  };

  const handlePhotoUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setUploadingPhoto(true);
    const newPhotos = [];

    for (const file of files) {
      try {
        const formDataUpload = new FormData();
        formDataUpload.append('file', file);

        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/upload/photo`,
          {
            method: 'POST',
            credentials: 'include',
            body: formDataUpload,
          }
        );

        if (response.ok) {
          const result = await response.json();
          newPhotos.push({
            file_id: result.file_id,
            path: result.path,
            filename: result.filename
          });
        } else {
          toast.error(`Échec upload: ${file.name}`);
        }
      } catch (error) {
        logError('Upload failed:', error);
        toast.error(`Erreur: ${file.name}`);
      }
    }

    setUploadedPhotos(prev => [...prev, ...newPhotos]);
    if (newPhotos.length > 0) {
      toast.success(`${newPhotos.length} photo(s) uploadée(s)`);
    }
    setUploadingPhoto(false);
  };

  const removePhoto = (fileId) => {
    setUploadedPhotos(prev => prev.filter(p => p.file_id !== fileId));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        photos: uploadedPhotos.map(p => p.file_id)
      };

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/reports`,
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        }
      );

      if (response.ok) {
        toast.success('Rapport créé');
        setIsDialogOpen(false);
        resetForm();
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erreur');
      }
    } catch (error) {
      logError('Failed to create report:', error);
      toast.error('Erreur lors de la création');
    }
  };

  const resetForm = () => {
    setFormData({
      client_id: '',
      site_name: '',
      visit_date: '',
      observations: '',
      recommendations: '',
      zone_id: zones.length > 0 ? zones[0].zone_id : '',
      priority: 'normal',
    });
    setUploadedPhotos([]);
  };

  const getClientName = (clientId) => {
    const client = clients.find(c => c.client_id === clientId);
    return client ? client.name : clientId;
  };

  const getZoneName = (zoneId) => {
    const zone = zones.find(z => z.zone_id === zoneId);
    return zone ? zone.name : zoneId;
  };

  const getStatusBadge = (status) => {
    const config = {
      draft: { label: 'Brouillon', className: 'bg-[#94A3B8]/20 text-[#94A3B8] border-[#94A3B8]/30' },
      submitted: { label: 'Soumis', className: 'bg-[#2563EB]/20 text-[#2563EB] border-[#2563EB]/30' },
      reviewed: { label: 'Examiné', className: 'bg-[#10B981]/20 text-[#10B981] border-[#10B981]/30' },
    };
    const { label, className } = config[status] || config.submitted;
    return <Badge className={`${className} border`}>{label}</Badge>;
  };

  const handleDeleteReport = async (reportId) => {
    if (!window.confirm('Supprimer ce rapport ?')) return;
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/reports/${reportId}`, { method: 'DELETE', credentials: 'include' });
      if (res.ok) { toast.success('Rapport supprimé'); fetchData(); }
      else { const err = await res.json(); toast.error(err.detail || 'Erreur'); }
    } catch { toast.error('Erreur'); }
  };

  const canEdit = userPermissions?.effective?.reports?.write;
  const { sortConfig, onSort, sortData } = useSort('visit_date', 'desc');

  if (loading) {
    return <div className="text-[#94A3B8]">{t('common.loading')}</div>;
  }

  return (
    <div className="space-y-6" data-testid="reports-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-4xl font-bold text-[#F8FAFC] tracking-tight">{t('reports.title')}</h1>
          <p className="text-[#94A3B8] mt-1 text-sm sm:text-base">Rapports de visites et inspections</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          {/* Zone Filter */}
          {userPermissions?.can_view_all_zones && (
            <div className="flex items-center gap-2">
              <Funnel size={20} className="text-[#94A3B8]" />
              <div className="w-[180px]">
              <SearchableSelect value={selectedZone} onValueChange={setSelectedZone} options={[{ value: 'all', label: 'Toutes les zones' }, ...zones.map(z => ({ value: z.zone_id, label: z.name }))]} placeholder="Toutes les zones" testId="zone-filter" />
            </div>
            </div>
          )}
        
        {canEdit && (
          <Button
            type="button"
            variant="outline"
            onClick={() => setAggregateOpen(true)}
            className="border-[#334155] text-[#F8FAFC] hover:bg-[#1E293B]"
            data-testid="open-aggregate-btn"
          >
            <FileArrowDown size={18} className="mr-2" weight="bold" />
            Télécharger rapports client
          </Button>
        )}

        {canEdit && (
        <Dialog open={isDialogOpen} onOpenChange={(open) => {
          setIsDialogOpen(open);
          if (!open) resetForm();
        }}>
          <DialogTrigger asChild>
            <Button className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white" data-testid="add-report-button">
              <Plus size={20} className="mr-2" weight="bold" />
              {t('reports.add')}
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-[#0F172A] border-[#1E293B] text-[#F8FAFC] max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-2xl font-bold">{t('reports.add')}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="client_id" className="text-[#F8FAFC]">{t('reports.client')}</Label>
                  <div className="mt-1">
                    <SearchableSelect value={formData.client_id || ''} onValueChange={(value) => setFormData({ ...formData, client_id: value, site_name: '' })} options={clients.map(c => ({ value: c.client_id, label: c.name }))} placeholder="Sélectionner un client" searchPlaceholder="Rechercher un client..." testId="report-client-select" />
                  </div>
                </div>
                <div>
                  <Label htmlFor="visit_date" className="text-[#F8FAFC]">{t('reports.visitDate')}</Label>
                  <Input
                    id="visit_date"
                    type="datetime-local"
                    value={formData.visit_date}
                    onChange={(e) => setFormData({ ...formData, visit_date: e.target.value })}
                    className="bg-[#1E293B] border-[#334155] text-[#F8FAFC]"
                    data-testid="visit-date-input"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="site_name" className="text-[#F8FAFC]">{t('reports.site')}</Label>
                <div className="mt-1">
                  {(() => {
                    const clientSites = clients.find(c => c.client_id === formData.client_id)?.sites || [];
                    return clientSites.length > 0 ? (
                      <SearchableSelect value={formData.site_name} onValueChange={(v) => setFormData({ ...formData, site_name: v })} options={clientSites.map(s => ({ value: s, label: s }))} placeholder="Sélectionner le site du client" searchPlaceholder="Rechercher un site..." testId="report-site-select" />
                    ) : (
                      <Input
                        id="site_name"
                        value={formData.site_name}
                        onChange={(e) => setFormData({ ...formData, site_name: e.target.value })}
                        required
                        placeholder="Nom du site"
                        className="bg-[#1E293B] border-[#334155] text-[#F8FAFC]"
                        data-testid="report-site-input"
                      />
                    );
                  })()}
                </div>
              </div>
              <div>
                <Label htmlFor="observations" className="text-[#F8FAFC]">{t('reports.observations')}</Label>
                <Textarea
                  id="observations"
                  value={formData.observations}
                  onChange={(e) => setFormData({ ...formData, observations: e.target.value })}
                  required
                  rows={4}
                  className="bg-[#1E293B] border-[#334155] text-[#F8FAFC]"
                  data-testid="observations-textarea"
                />
              </div>
              <div>
                <Label htmlFor="recommendations" className="text-[#F8FAFC]">{t('reports.recommendations')}</Label>
                <Textarea
                  id="recommendations"
                  value={formData.recommendations}
                  onChange={(e) => setFormData({ ...formData, recommendations: e.target.value })}
                  required
                  rows={4}
                  className="bg-[#1E293B] border-[#334155] text-[#F8FAFC]"
                  data-testid="recommendations-textarea"
                />
              </div>

              <label className="flex items-center gap-3 p-3 rounded-md border border-[#EF4444]/30 bg-[#EF4444]/5 cursor-pointer" data-testid="report-urgent-toggle-label">
                <input
                  type="checkbox"
                  checked={formData.priority === 'urgent'}
                  onChange={(e) => setFormData({ ...formData, priority: e.target.checked ? 'urgent' : 'normal' })}
                  className="h-4 w-4 accent-[#EF4444]"
                  data-testid="report-urgent-toggle"
                />
                <div>
                  <p className="text-sm font-medium text-[#EF4444]">Rapport urgent</p>
                  <p className="text-xs text-[#94A3B8]">Envoie immédiatement une alerte (email + push) aux superviseurs et admins.</p>
                </div>
              </label>
              {/* Photo Upload Section */}
              <div>
                <Label className="text-[#F8FAFC]">Photos</Label>
                <div className="mt-2 border-2 border-dashed border-[#334155] rounded-md p-4">
                  <div className="flex items-center justify-center">
                    <label className="cursor-pointer flex flex-col items-center">
                      <UploadSimple size={32} className="text-[#94A3B8] mb-2" />
                      <span className="text-sm text-[#94A3B8]">
                        {uploadingPhoto ? 'Upload en cours...' : 'Cliquez pour ajouter des photos'}
                      </span>
                      <input
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={handlePhotoUpload}
                        disabled={uploadingPhoto}
                        className="hidden"
                      />
                    </label>
                  </div>
                  {uploadedPhotos.length > 0 && (
                    <div className="mt-4 flex flex-wrap gap-2">
                      {uploadedPhotos.map(photo => (
                        <div key={photo.file_id} className="relative bg-[#1E293B] rounded-md p-2 flex items-center gap-2">
                          <ImageIcon size={16} className="text-[#2563EB]" />
                          <span className="text-xs text-[#F8FAFC] truncate max-w-[100px]">{photo.filename}</span>
                          <button
                            type="button"
                            onClick={() => removePhoto(photo.file_id)}
                            className="text-[#EF4444] hover:text-white"
                          >
                            <X size={14} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsDialogOpen(false)}
                  className="border-[#334155] text-[#F8FAFC] hover:bg-[#1E293B]"
                >
                  {t('common.cancel')}
                </Button>
                <Button type="submit" className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white" data-testid="save-report-button">
                  {t('common.save')}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
        )}
        </div>
      </div>

      {/* Bannière Rapport consolidé */}
      {canEdit && (
        <div
          className="bg-gradient-to-r from-[#1E3A8A]/40 to-[#0F172A] border border-[#2563EB]/30 rounded-lg p-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3"
          data-testid="aggregate-banner"
        >
          <div className="flex items-start gap-3">
            <div className="h-10 w-10 rounded-lg bg-[#2563EB]/20 flex items-center justify-center flex-shrink-0">
              <FileArrowDown size={20} className="text-[#60A5FA]" weight="duotone" />
            </div>
            <div>
              <h3 className="text-sm sm:text-base font-semibold text-[#F8FAFC]">Rapport consolidé par client</h3>
              <p className="text-xs text-[#94A3B8] mt-0.5">
                Un seul PDF regroupant tous les sites d&apos;un client (journée ou semaine). Sites sans rapport → R.A.S.
              </p>
            </div>
          </div>
          <Button
            type="button"
            onClick={() => setAggregateOpen(true)}
            className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white flex-shrink-0"
            data-testid="aggregate-banner-btn"
          >
            <FileArrowDown size={16} className="mr-2" weight="bold" />
            Générer le PDF
          </Button>
        </div>
      )}

      {/* Search & Date Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Input value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} placeholder="Rechercher par client ou site..." className="bg-[#0F172A] border-[#1E293B] text-[#F8FAFC]" data-testid="search-report" />
        </div>
        <div className="flex gap-2">
          <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="bg-[#0F172A] border-[#1E293B] text-[#F8FAFC] w-[150px]" placeholder="Du" />
          <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="bg-[#0F172A] border-[#1E293B] text-[#F8FAFC] w-[150px]" placeholder="Au" />
        </div>
        <Select value={sortConfig.key} onValueChange={(v) => onSort(v)}>
          <SelectTrigger className="w-[180px] bg-[#1E293B] border-[#334155] text-[#F8FAFC]" data-testid="sort-reports">
            <SelectValue placeholder="Trier par..." />
          </SelectTrigger>
          <SelectContent className="bg-[#0F172A] border-[#1E293B]">
            <SelectItem value="visit_date" className="text-[#F8FAFC]">Date de visite</SelectItem>
            <SelectItem value="client_name" className="text-[#F8FAFC]">Client (A-Z)</SelectItem>
            <SelectItem value="site_name" className="text-[#F8FAFC]">Site (A-Z)</SelectItem>
            <SelectItem value="status" className="text-[#F8FAFC]">Statut</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {(() => {
          const filtered = sortData(reports.filter(r => {
            const q = searchQuery.toLowerCase();
            const matchSearch = !searchQuery || r.site_name?.toLowerCase().includes(q) || clients.find(c => c.client_id === r.client_id)?.name?.toLowerCase().includes(q);
            const rDate = r.visit_date?.split('T')[0];
            const matchFrom = !dateFrom || rDate >= dateFrom;
            const matchTo = !dateTo || rDate <= dateTo;
            return matchSearch && matchFrom && matchTo;
          }), (item, key) => {
            if (key === 'client_name') return clients.find(c => c.client_id === item.client_id)?.name || '';
            return item[key];
          });
          return filtered.length === 0 ? (
          <div className="col-span-2 bg-[#0F172A] border border-[#1E293B] rounded-md p-12 text-center">
            <ImageIcon size={48} className="mx-auto text-[#94A3B8] mb-4" weight="duotone" />
            <p className="text-[#94A3B8]">Aucun rapport disponible</p>
          </div>
        ) : filtered.map((report) => (
            <div
              key={report.report_id}
              className="bg-[#0F172A] border border-[#1E293B] rounded-md p-6 hover:border-[#334155] transition-colors"
              data-testid={`report-card-${report.report_id}`}
            >
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="text-lg font-bold text-[#F8FAFC]">{getClientName(report.client_id)}</h3>
                  <p className="text-sm text-[#94A3B8] mt-1">{report.site_name}</p>
                </div>
                <div className="flex items-center gap-2">
                  {getStatusBadge(report.status)}
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => handleExportPDF(report.report_id)}
                    className="text-[#2563EB] hover:text-white hover:bg-[#2563EB]"
                    title="Exporter en PDF"
                  >
                    <FilePdf size={18} weight="duotone" />
                  </Button>
                  {userRole === 'admin' && (
                    <Button size="sm" variant="ghost" onClick={() => handleDeleteReport(report.report_id)} className="text-[#EF4444] hover:text-white hover:bg-[#EF4444]" title="Supprimer">
                      <Trash size={18} />
                    </Button>
                  )}
                </div>
              </div>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-[#94A3B8] uppercase tracking-wide mb-1">Date de visite</p>
                    <p className="text-sm text-[#F8FAFC]">{new Date(report.visit_date).toLocaleString('fr-FR')}</p>
                  </div>
                  <Badge className="bg-[#2563EB]/20 text-[#2563EB] border border-[#2563EB]/30">
                    {getZoneName(report.zone_id)}
                  </Badge>
                </div>
                <div>
                  <p className="text-xs text-[#94A3B8] uppercase tracking-wide mb-1">Observations</p>
                  <p className="text-sm text-[#F8FAFC] line-clamp-2">{report.observations}</p>
                </div>
                <div>
                  <p className="text-xs text-[#94A3B8] uppercase tracking-wide mb-1">Recommandations</p>
                  <p className="text-sm text-[#F8FAFC] line-clamp-2">{report.recommendations}</p>
                </div>
              </div>
            </div>
          ))
        })()}
      </div>

      {/* Dialog Rapport consolidé */}
      <Dialog open={aggregateOpen} onOpenChange={setAggregateOpen}>
        <DialogContent className="bg-[#0F172A] border-[#1E293B] text-[#F8FAFC] max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold flex items-center gap-2">
              <FileArrowDown size={20} className="text-[#2563EB]" />
              Rapport consolidé par client
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-xs text-[#94A3B8]">
              Regroupe dans un même PDF tous les rapports d&apos;un client (tous ses sites) sur la période choisie.
              Les sites sans rapport sur la période afficheront <b>R.A.S.</b> (Rien à signaler).
            </p>
            <div>
              <Label className="text-[#F8FAFC]">Client *</Label>
              <div className="mt-1">
                <SearchableSelect
                  value={aggregateClientId}
                  onValueChange={setAggregateClientId}
                  options={clients.map(c => ({ value: c.client_id, label: c.name }))}
                  placeholder="Sélectionner un client"
                  searchPlaceholder="Rechercher un client..."
                  testId="aggregate-client-select"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[#F8FAFC]">Du</Label>
                <Input
                  type="date"
                  value={aggregateFrom}
                  onChange={(e) => setAggregateFrom(e.target.value)}
                  className="bg-[#1E293B] border-[#334155] text-[#F8FAFC] mt-1"
                  data-testid="aggregate-from"
                />
              </div>
              <div>
                <Label className="text-[#F8FAFC]">Au</Label>
                <Input
                  type="date"
                  value={aggregateTo}
                  onChange={(e) => setAggregateTo(e.target.value)}
                  className="bg-[#1E293B] border-[#334155] text-[#F8FAFC] mt-1"
                  data-testid="aggregate-to"
                />
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button type="button" size="sm" variant="outline" onClick={() => setQuickPeriod('today')} className="border-[#334155] text-[#F8FAFC] hover:bg-[#1E293B] text-xs" data-testid="quick-today">Aujourd&apos;hui</Button>
              <Button type="button" size="sm" variant="outline" onClick={() => setQuickPeriod('week')} className="border-[#334155] text-[#F8FAFC] hover:bg-[#1E293B] text-xs" data-testid="quick-week">Cette semaine</Button>
              <Button type="button" size="sm" variant="outline" onClick={() => setQuickPeriod('month')} className="border-[#334155] text-[#F8FAFC] hover:bg-[#1E293B] text-xs" data-testid="quick-month">Ce mois</Button>
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setAggregateOpen(false)}
                className="border-[#334155] text-[#F8FAFC] hover:bg-[#1E293B]"
              >
                Annuler
              </Button>
              <Button
                type="button"
                onClick={handleAggregatePDF}
                disabled={aggregateBusy || !aggregateClientId}
                className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white"
                data-testid="aggregate-download-btn"
              >
                <FileArrowDown size={16} className="mr-2" weight="bold" />
                {aggregateBusy ? 'Génération...' : 'Télécharger le PDF'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Reports;
